import React, { useMemo } from "react";
import { Info } from "lucide-react";
import ScholarshipSourcesCarousel from "./ScholarshipSourcesCarousel";

/**
 * @typedef {Object} ScholarshipsOverviewMetric
 * @property {string} id
 * @property {string} label
 * @property {string|null} value
 */

/**
 * @typedef {Object} ScholarshipsOverview
 * @property {ScholarshipsOverviewMetric[]} metrics
 * @property {string|null} takeaway
 */

/**
 * @typedef {Object} ScholarshipPathwayStep
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {"Strong" | "Moderate" | "Limited" | null} strength
 */

/**
 * @typedef {Object} SportCommitmentRow
 * @property {string} id
 * @property {string} sport
 * @property {string} avgCommits
 * @property {string} highestLevel
 * @property {string} exampleColleges
 */

/**
 * @typedef {Object} DivisionDistributionBar
 * @property {string} id
 * @property {string} label
 * @property {number} value
 */

/**
 * @typedef {Object} DivisionDistribution
 * @property {DivisionDistributionBar[]} bars
 * @property {string|null} callout
 */

/**
 * @typedef {Object} ScholarshipsData
 * @property {ScholarshipsOverview} overview
 * @property {ScholarshipPathwayStep[]} pathway
 * @property {SportCommitmentRow[]} commitments
 * @property {DivisionDistribution} divisions
 * @property {string|null} sources
 * @property {string|null} lastVerified
 */

const TOOLTIP_COPY = {
  athletes:
    "Approximate number of student-athletes from this school who go on to play sports in college each year. Includes scholarship and non-scholarship roster spots.",
  divisions:
    "Counts of college destinations by level (D1, D2, D3, NAIA/JUCO) based on available commitment/signing data. D3 schools don’t offer athletic scholarships, but athletes can still commit to play.",
  annualValue:
    "Approximate total athletic aid value per year, inferred from the number of college athletes and typical scholarship ranges by sport/division. Actual awards vary and may be partial.",
  sports:
    "The sports that most often produce college placements from this school based on recent commitment/signing information.",
};

const COMMITMENT_TOOLTIP_COPY = {
  section:
    "What this shows: Estimated college placements by sport based on recent commitment/signing information. 'Avg commits/year' is a typical yearly range, and 'Highest level' is the top competitive level reached by athletes from this school. College destinations are examples of programs athletes have committed to.",
  avgCommits:
    "Typical number of athletes per year who go on to play this sport in college. This is a range, not an exact count.",
  highestLevel:
    "The highest competition level reached in recent years for this sport (D1/D2/D3/NAIA/JUCO).",
  destinations:
    "Examples of colleges athletes have committed to for this sport. Not a complete list.",
};

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric;
};

const formatCount = (value) => {
  const numeric = toNumber(value);
  if (numeric == null) return null;
  return Number.isInteger(numeric)
    ? String(numeric)
    : String(Math.round(numeric));
};

const formatRange = (minValue, maxValue, suffix) => {
  const min = formatCount(minValue);
  const max = formatCount(maxValue);
  if (min && max) {
    return `~${min}-${max} ${suffix}`.trim();
  }
  if (min) {
    return `~${min}+ ${suffix}`.trim();
  }
  if (max) {
    return `Up to ${max} ${suffix}`.trim();
  }
  return null;
};

const formatCompactCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric >= 1_000_000_000) {
    const out = (numeric / 1_000_000_000).toFixed(1).replace(/\.0$/, "");
    return `$${out}B`;
  }
  if (numeric >= 1_000_000) {
    const out = (numeric / 1_000_000).toFixed(1).replace(/\.0$/, "");
    return `$${out}M`;
  }
  if (numeric >= 1_000) {
    const out = (numeric / 1_000).toFixed(1).replace(/\.0$/, "");
    return `$${out}K`;
  }
  return `$${numeric.toLocaleString("en-US")}`;
};

const formatCurrencyRange = (minValue, maxValue) => {
  const min = toNumber(minValue);
  const max = toNumber(maxValue);
  const minLabel = min != null ? formatCompactCurrency(min) : null;
  const maxLabel = max != null ? formatCompactCurrency(max) : null;
  if (minLabel && maxLabel) return `${minLabel} - ${maxLabel}`;
  if (minLabel) return minLabel;
  if (maxLabel) return maxLabel;
  return null;
};

const parseJsonValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeRangeText = (value) => {
  if (!value) return value;
  return String(value)
    .replace(/\u00e2\u20ac\u201c/g, "-")
    .replace(/[\u2013\u2014]/g, "-")
    .trim();
};

const normalizeStrength = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "strong") return "Strong";
  if (normalized === "moderate") return "Moderate";
  if (normalized === "limited") return "Limited";
  return null;
};

const normalizeExampleColleges = (value) => {
  if (!value) return "Not available";
  const cleaned = String(value).trim();
  if (!cleaned) return "Not available";
  const lowered = cleaned.toLowerCase();
  if (lowered === "see source links" || lowered === "see sources") {
    return "Not available";
  }
  return cleaned;
};

const buildScholarshipsData = (source) => {
  if (!source) return null;

  const scholarshipsRange = formatRange(
    source.sch_estimated_scholarships_min,
    source.sch_estimated_scholarships_max,
    "athletes"
  );
  const d1Count = formatCount(source.sch_division_d1_count);
  const d2Count = formatCount(source.sch_division_d2_count);
  const d3Count = toNumber(source.sch_division_d3_count);
  const naiaCount = toNumber(source.sch_division_naia_juco_count);
  const d3NaiaCombined =
    d3Count != null || naiaCount != null
      ? formatCount((d3Count || 0) + (naiaCount || 0))
      : null;
  const divisionLine1Parts = [
    d1Count ? `D1: ${d1Count}` : null,
    d2Count ? `D2: ${d2Count}` : null,
  ].filter(Boolean);
  const divisionLine2 = d3NaiaCombined ? `D3/NAIA: ${d3NaiaCombined}` : null;
  const divisionValue =
    divisionLine1Parts.length || divisionLine2
      ? [divisionLine1Parts.join(" | "), divisionLine2]
          .filter(Boolean)
          .join("\n")
      : null;
  const annualValue = formatCurrencyRange(
    source.sch_estimated_value_min_usd,
    source.sch_estimated_value_max_usd
  );

  const commitmentsJson = parseJsonValue(source.sch_commitment_breakdown_json);
  const commitmentRows = Array.isArray(commitmentsJson)
    ? commitmentsJson
        .map((row, index) => ({
          id: row?.sport ? String(row.sport) : `row-${index}`,
          sport: row?.sport ?? "Unknown sport",
          avgCommits: normalizeRangeText(row?.avg_commits_per_year) ?? "N/A",
          highestLevel: row?.highest_level ?? "N/A",
          exampleColleges: Array.isArray(row?.example_colleges)
            ? normalizeExampleColleges(row.example_colleges.join(", "))
            : normalizeExampleColleges(row?.example_colleges),
        }))
        .filter((row) => row.sport && row.sport !== "Unknown sport")
    : [];

  const divisionsJson = parseJsonValue(source.sch_division_distribution_json);
  const divisionBars = divisionsJson
    ? [
        { id: "d1", label: "D1", value: Number(divisionsJson.d1) },
        { id: "d2", label: "D2", value: Number(divisionsJson.d2) },
        { id: "d3", label: "D3", value: Number(divisionsJson.d3) },
        {
          id: "naia",
          label: "NAIA/JUCO",
          value: Number(divisionsJson.naia_juco),
        },
      ].filter((bar) => Number.isFinite(bar.value))
    : [];

  const pathway = [
    {
      id: "varsity",
      title: "Varsity Pipeline",
      description: "Depth of varsity teams feeding college-ready talent.",
      strength: normalizeStrength(source.sch_path_varsity_pipeline),
    },
    {
      id: "competition",
      title: "Competition Level",
      description: "League play and postseason reach shaping visibility.",
      strength: normalizeStrength(source.sch_path_competition_level),
    },
    {
      id: "visibility",
      title: "Recruiting Visibility",
      description: "Coach engagement, showcases, and scouting touchpoints.",
      strength: normalizeStrength(source.sch_path_recruiting_visibility),
    },
    {
      id: "commitments",
      title: "Commitments & Offers",
      description: "Recent signed athletes and reported offers.",
      strength: normalizeStrength(source.sch_path_commitments_offers),
    },
  ];

  const hasAnyRequiredPresence = Boolean(
    source.sch_estimated_scholarships_min != null ||
      source.sch_estimated_scholarships_max != null ||
      source.sch_division_d1_count != null ||
      source.sch_division_d2_count != null ||
      source.sch_division_d3_count != null ||
      source.sch_division_naia_juco_count != null ||
      source.sch_commitment_breakdown_json ||
      source.sch_division_distribution_json
  );
  const hasAnyOverview =
    scholarshipsRange ||
    divisionValue ||
    annualValue ||
    source.sch_primary_sports ||
    source.sch_key_takeaway;
  const hasAnyCommitments = commitmentRows.length > 0;
  const hasAnyDivisions = divisionBars.length > 0;
  const hasAnyPathway = pathway.some((step) => step.strength);

  if (
    !hasAnyRequiredPresence &&
    !hasAnyOverview &&
    !hasAnyCommitments &&
    !hasAnyDivisions &&
    !hasAnyPathway
  ) {
    return null;
  }

  return {
    overview: {
      metrics: [
        {
          id: "athletes",
          label: "Estimated Athletic Scholarships / Year",
          value: scholarshipsRange,
        },
        {
          id: "divisions",
          label: "Division Commitments",
          value: divisionValue,
        },
        {
          id: "annualValue",
          label: "Estimated Annual Scholarship Value",
          value: annualValue,
        },
        {
          id: "sports",
          label: "Primary Sports Driving Scholarships",
          value: source.sch_primary_sports,
        },
      ],
      takeaway: source.sch_key_takeaway,
    },
    pathway,
    commitments: commitmentRows,
    divisions: {
      bars: divisionBars,
      callout: hasAnyDivisions
        ? "Estimated: Above national average in D1 commitments."
        : null,
    },
    sources: source.sch_sources_urls,
    lastVerified: source.sch_last_verified,
  };
};

const ScholarshipsTab = ({ profile, schoolSummary }) => {
  const data = useMemo(
    () => buildScholarshipsData(profile ?? schoolSummary),
    [profile, schoolSummary]
  );

  if (!data) {
    return (
      <EmptyState
        title="Not enough data yet"
        description="Scholarship insights will appear when available."
      />
    );
  }

  const { overview, pathway, commitments, divisions, sources, lastVerified } =
    data;
  const handleScrollToSources = () => {
    const target = document.getElementById("scholarship-sources");
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">
              Scholarships
            </p>
            <h2 className="text-xl font-unbounded">At a glance</h2>
          </div>
        </div>

        {/* UPDATED GRID: 
          Uses grid-cols-1 for mobile (default) to ensure full width for long labels 
          and prevent tooltips from being cut off. 
          Switches to 2 columns on 'sm' screens and 4 columns on 'lg' screens.
        */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          {overview?.metrics?.map((metric) => (
            <div
              key={metric.id}
              className="relative flex flex-col justify-between rounded-xl border border-[#FDE6D2] bg-[#FFF8F4] p-5 transition-all hover:shadow-md hover:z-10"
            >
              {/* Top Row: Label & Info Icon */}
              <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gray-500 pr-6 leading-relaxed">
                  {metric.label}
                </p>
                {/* Absolutely positioned Info Icon at top right */}
                <div className="absolute top-3 right-3">
                  <InfoTooltip text={TOOLTIP_COPY[metric.id]} />
                </div>
              </div>

              {/* Bottom Row: Value */}
              <div className="mt-2">
                <p
                  className={`text-lg md:text-xl font-bold text-[#111827] leading-tight ${
                    metric.id === "divisions" ? "whitespace-pre-line" : ""
                  }`}
                >
                  {metric.value ?? "Not enough data yet"}
                </p>
              </div>
            </div>
          ))}
        </div>

        {overview?.takeaway && (
          <div className="mt-5 rounded-lg bg-gray-50 p-4 border border-gray-100">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-[#111827] block mb-1">
                Key takeaway:
              </span>{" "}
              {overview.takeaway}
            </p>
          </div>
        )}

        {sources && (
          <button
            type="button"
            onClick={handleScrollToSources}
            className="mt-4 inline-flex text-xs font-medium uppercase tracking-wide text-[#F07639] hover:underline hover:underline-offset-4"
          >
            See sources
          </button>
        )}
      </section>

      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-unbounded">Path to athletic scholarships</h3>
        </div>
        <div className="mt-5 space-y-4">
          {pathway?.map((step, index) => (
            <div key={step.id} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="h-3 w-3 rounded-full bg-[#F07639]" />
                {index < pathway.length - 1 && (
                  <div className="w-px flex-1 bg-[#E5E7EB] mt-2" />
                )}
              </div>
              <div className="flex-1 pb-4 border-b border-[#F3F4F6] last:border-b-0">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-[#111827]">{step.title}</p>
                  {step.strength ? (
                    <StrengthBadge strength={step.strength} />
                  ) : (
                    <span className="text-xs text-gray-500">
                      Not enough data yet
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-unbounded">Commitment breakdown</h3>
          <InfoTooltip text={COMMITMENT_TOOLTIP_COPY.section} />
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Estimated commitments by sport and competitive level.
        </p>

        {commitments.length === 0 ? (
          <div className="mt-6 text-sm text-gray-500">
            Not enough data yet.
          </div>
        ) : (
          <>
            <div className="mt-6 hidden md:grid grid-cols-4 text-xs uppercase tracking-[0.15em] font-medium text-gray-500 border-b pb-2">
              <span>Sport</span>
              <span className="inline-flex items-center gap-2">
                Avg commits/year
                <InfoTooltip text={COMMITMENT_TOOLTIP_COPY.avgCommits} />
              </span>
              <span className="inline-flex items-center gap-2">
                Highest level
                <InfoTooltip text={COMMITMENT_TOOLTIP_COPY.highestLevel} />
              </span>
              <span className="inline-flex items-center gap-2">
                College destinations
                <InfoTooltip text={COMMITMENT_TOOLTIP_COPY.destinations} />
              </span>
            </div>
            <div className="mt-3 space-y-3">
              {commitments?.map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 transition-colors hover:bg-[#FFF8F4] hover:border-[#FDE6D2]"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm text-[#111827]">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 md:hidden mb-1">
                        Sport
                      </p>
                      <p className="font-semibold">{row.sport}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 md:hidden mb-1">
                        Avg commits/year
                      </p>
                      <p>{row.avgCommits}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 md:hidden mb-1">
                        Highest level
                      </p>
                      <p>{row.highestLevel}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 md:hidden mb-1">
                        College destinations
                      </p>
                      <p className="text-gray-700 leading-snug">
                        {row.exampleColleges}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-unbounded">Division distribution</h3>
        </div>

        {divisions?.bars?.length ? (
          <>
            <div className="mt-5 space-y-4">
              {divisions?.bars?.map((bar) => (
                <div key={bar.id} className="space-y-1">
                  <div className="flex items-center justify-between text-sm text-gray-700 font-medium">
                    <span>{bar.label}</span>
                    <span>{bar.value}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#F07639]"
                      style={{ width: `${bar.value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {divisions?.callout && (
              <p className="mt-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
                {divisions.callout}
              </p>
            )}
          </>
        ) : (
          <p className="mt-4 text-sm text-gray-500">Not enough data yet.</p>
        )}
      </section>

      {sources && (
        <section
          id="scholarship-sources"
          className="rounded-xl border bg-white p-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-unbounded">Related coverage</h3>
            </div>
          </div>
          <div className="mt-5">
            <ScholarshipSourcesCarousel
              sources={sources}
              lastVerified={lastVerified}
            />
          </div>
        </section>
      )}

      <section className="rounded-xl border bg-[#FFF8F4] p-6">
        <p className="text-sm font-semibold text-[#1F2937] mb-2">
          Disclaimer:
        </p>
        <p className="text-sm text-[#1F2937] leading-relaxed">
          We’re actively working to collect and refresh scholarship and
          commitment information from trusted sources. Some values may be
          estimated when complete public data isn’t available, and details may
          change over time. Please verify final scholarship offers and roster
          decisions directly with the college program.
        </p>
      </section>
    </div>
  );
};

const StrengthBadge = ({ strength }) => {
  const styles = {
    Strong: "border-[#F2CDA1] bg-[#FFF3ED] text-[#F07639]",
    Moderate: "border-[#E5E7EB] bg-white text-[#475569]",
    Limited: "border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B]",
  };
  return (
    <span
      className={`rounded-full border px-3 py-1 text-xs font-semibold ${
        styles[strength] || styles.Moderate
      }`}
    >
      {strength}
    </span>
  );
};

const InfoTooltip = ({ text, className = "" }) => (
  <span
    className={`group relative inline-flex items-center cursor-help ${className}`}
  >
    <Info className="h-4 w-4 text-[#9CA3AF] transition-colors group-hover:text-[#F07639]" />
    <span className="pointer-events-none absolute right-0 top-7 z-50 w-[200px] rounded-lg bg-[#111827] px-3 py-2 text-left text-[11px] leading-snug text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 normal-case">
      {text}
    </span>
  </span>
);

const EmptyState = ({ title, description }) => (
  <div className="text-center py-12 border rounded-xl bg-[#FDF8F3]">
    <h3 className="text-xl font-unbounded">{title}</h3>
    <p className="text-gray-600 mt-2">{description}</p>
  </div>
);

export default ScholarshipsTab;